import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { 
  XMarkIcon, 
  BuildingOfficeIcon, 
  ExclamationTriangleIcon 
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import api from '../services/api';

interface Engagement {
  id: string;
  name: string;
  number: string;
  cloudProvider: string;
  status: string;
  Customer: {
    id: string;
    accountName: string;
    accountShortName: string;
  };
}

// EngagementPlan interface removed - service plans not used

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onEngagementSelect: (engagement: {id: string, name: string}) => void;
}

const EngagementSelectionModal: React.FC<Props> = ({ 
  isOpen, 
  onClose, 
  onEngagementSelect 
}) => {
  const [loading, setLoading] = useState(false);
  const [engagements, setEngagements] = useState<Engagement[]>([]);
  // Service plan selection removed
  const [error, setError] = useState<string | null>(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setError(null);
      fetchEngagements();
    }
  }, [isOpen]);

  const fetchEngagements = async () => {
    try {
      setLoading(true);
      setError(null);
      // Fetch all engagements (not just ACTIVE ones) to be more inclusive
      const response = await api.get('/engagements?limit=100');
      
      // Filter engagements that have cloud provider configured
      const cloudEngagements = response.data.engagements.filter((eng: Engagement) => 
        eng.cloudProvider && eng.cloudProvider !== 'OnPremise'
      );
      
      setEngagements(cloudEngagements);
      
      if (cloudEngagements.length === 0) {
        // More helpful error message with troubleshooting info
        const totalEngagements = response.data.engagements.length;
        if (totalEngagements === 0) {
          setError('No engagements found. Please create an engagement first.');
        } else {
          const onPremiseCount = response.data.engagements.filter((eng: Engagement) => 
            eng.cloudProvider === 'OnPremise'
          ).length;
          const noProviderCount = response.data.engagements.filter((eng: Engagement) => 
            !eng.cloudProvider
          ).length;
          
          let message = `Found ${totalEngagements} engagement(s), but none have cloud provider configuration. `;
          if (noProviderCount > 0) {
            message += `${noProviderCount} engagement(s) have no cloud provider set. `;
          }
          if (onPremiseCount > 0) {
            message += `${onPremiseCount} engagement(s) are set to OnPremise. `;
          }
          message += 'Please edit an engagement and configure Azure, AWS, or GCP details.';
          
          setError(message);
        }
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to fetch engagements';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Service plan fetching removed - going directly to resource sync

  const handleEngagementSelect = (engagement: Engagement) => {
    // Skip service plan selection and go directly to resource sync
    onEngagementSelect({
      id: engagement.id,
      name: engagement.name
    });
  };

  // Service plan selection removed

  // Continue method removed - selection happens immediately

  const handleClose = () => {
    onClose();
  };

  // Back navigation removed - single step flow

  const renderEngagementSelection = () => (
    <div>
      <div className="mb-4">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          Select Engagement
        </h3>
        <p className="mt-1 text-sm text-gray-600">
          Choose an engagement with cloud provider configuration to sync resources from
        </p>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading engagements...</p>
        </div>
      ) : engagements.length === 0 ? (
        <div className="text-center py-8">
          <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No engagements available</h3>
          <p className="mt-1 text-sm text-gray-500">
            No active engagements with cloud provider configuration found.
          </p>
        </div>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {engagements.map((engagement) => (
            <div
              key={engagement.id}
              onClick={() => handleEngagementSelect(engagement)}
              className="cursor-pointer rounded-lg border border-gray-200 p-4 hover:bg-gray-50 hover:border-gray-300 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center">
                    <h4 className="text-sm font-medium text-gray-900">{engagement.name}</h4>
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                      {engagement.cloudProvider}
                    </span>
                    <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      engagement.status === 'ACTIVE' 
                        ? 'bg-green-100 text-green-800' 
                        : engagement.status === 'DRAFT'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {engagement.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">{engagement.number}</p>
                  <p className="text-xs text-gray-400">Customer: {engagement.Customer.accountName}</p>
                </div>
                <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Service plan selection function removed - going directly to resource sync

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-6">
                  <Dialog.Title as="h2" className="text-xl font-semibold leading-6 text-gray-900">
                    Select Engagement
                  </Dialog.Title>
                  <button
                    onClick={handleClose}
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {error && (
                  <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex">
                      <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                      <div className="ml-3">
                        <p className="text-sm text-red-800">{error}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="min-h-[400px]">
                  {renderEngagementSelection()}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default EngagementSelectionModal;